import { message, Skeleton } from 'antd';
import { updateItemCart } from 'containers/MainLayout/actions';
import { selectBuyer, selectCart, selectStore, selectUserPermissions } from 'containers/MainLayout/selectors';
import { RootState } from 'containers/MainLayout/types';
import useMutationSaga, { GMutation } from 'hooks/useMutationSaga';
import useProductUomInfo from 'hooks/useProductUomInfo';
import { GQuery, useLazyQuerySaga } from 'hooks/useQuerySaga';
import React, { FC, useEffect, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import { connect } from 'react-redux';
import { Link } from 'react-router-dom';
import { Dispatch } from 'redux';
import { createStructuredSelector } from 'reselect';
import styled from 'styles/styled-components';
import translations from 'translations';
import {
  Buyer,
  Cart,
  CartItem,
  MutationCartPutItemLightArgs,
  MutationFavoriteAddItemArgs,
  MutationPutItemsToFavoriteArgs,
  Product,
  ProductSortField,
  QueryProductSearchArgs,
  SortDirection,
  Store,
} from 'types/schema';
import calculateVAT from 'utils/calculateVAT';
import { commonDimensions } from 'utils/constants';
import formatMoney from 'utils/formatMoney';
import generateProductSlug from 'utils/generateProductSlug';
import utilsMessages from 'utils/messages';
import { cartPutItemLight, favoriteAddItem, productSearch, putItemsToFavorite } from 'utils/query';
import FavoriteImage from './FavoriteImage.png';
import messages from './messages';

/* ─── Outer wrapper ──────────────────────────────────────────────────────────── */
const Wrapper = styled.div`
  max-width: ${commonDimensions.MAX_WIDTH_CONTAINER}px;
  width: 100%;
  margin: 0 auto;
  display: flex;
  gap: 16px;
  margin-bottom: 20px;
  padding: 0 40px;
  @media (max-width: 1023px) {
    flex-direction: column;
    padding: 0 16px;
  }
  @media (max-width: 767px) {
    padding: 0;
    gap: 0px;
    margin-bottom: 12px;
  }
`;

/* ─── Shared card ────────────────────────────────────────────────────────────── */
interface CardBaseProps {
  variant: 'green' | 'yellow';
}

interface StateProps {
  store: Store;
  permissions: string[];
  cart: Cart;
  buyer: Buyer;
}

interface IDispatchProps {
  updateItemCart: (payload: any) => void;
}

type IProps = StateProps & IDispatchProps;

const BG = {
  green: { bg: '#f5fbf5', border: '#c6f3c9', iconBg: '#e6f9e7', accent: '#43a047' },
  yellow: { bg: '#fffae9', border: '#ffda5b', iconBg: '#ffeeb2', accent: '#f38a3e' },
};

const CardBase = styled.div<CardBaseProps>`
  background: ${({ variant }) => BG[variant].bg};
  border: 1px solid ${({ variant }) => BG[variant].border};
  border-radius: 16px;
  padding: 20px;
  display: flex;
  flex-direction: column;
  gap: 16px;
  overflow: hidden;
  width: 100%;
  min-width: 0;
  @media (max-width: 991px) {
    padding: 16px 0;
  }
  @media (max-width: 767px) {
    border: none;
    border-radius: 0px;
  }
`;

/* ─── Card header ────────────────────────────────────────────────────────────── */
const CardHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  height: 74px;
  @media (max-width: 991px) {
    padding: 0 16px;
    align-items: flex-start;
    height: 60px;
  }
`;

const HeaderLeft = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  flex: 1;
  min-width: 0;
  overflow: hidden;
`;

const IconCircle = styled.div<CardBaseProps>`
  width: 40px;
  height: 40px;
  border-radius: 50%;
  background: ${({ variant }) => BG[variant].iconBg};
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  @media (max-width: 767px) {
    display: none;
  }
`;

const HeaderText = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
  @media (max-width: 767px) {
    gap: 0px;
  }
`;

const CardTitle = styled.span`
  font-size: 20px;
  font-weight: 700;
  line-height: 28px;
  color: #111827;
  @media (max-width: 767px) {
    font-size: 16px;
    font-weight: 600;
    line-height: 24px;
  }
`;

const CardSubtitle = styled.span`
  font-size: 14px;
  font-weight: 400;
  line-height: 22px;
  color: #303643;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 380px;
  @media (max-width: 767px) {
    font-size: 12px;
    font-weight: 400;
    line-height: 18px;
  }
  & > span > span {
    font-weight: 600;
  }
`;

/* ─── Action area (button / link) ───────────────────────────────────────────── */
const AddAllButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  height: 32px;
  padding: 0 12px;
  background: #43a047;
  color: #ffffff;
  font-size: 14px;
  font-weight: 500;
  line-height: 22px;
  border: none;
  border-radius: 6px;
  cursor: pointer;
  white-space: nowrap;
  flex-shrink: 0;
  transition: background 0.2s;
  &:hover {
    background: #388e3c;
  }
`;

const ViewAllLink = styled(Link)`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  font-size: 14px;
  font-weight: 500;
  line-height: 22px;
  color: #e56910;
  text-decoration: none;
  white-space: nowrap;
  flex-shrink: 0;
  &:hover {
    color: #c2550d;
  }
`;

/* ─── Product grid ───────────────────────────────────────────────────────────── */
const ProductGrid = styled.div`
  display: grid;
  grid-template-columns: minmax(0, 1fr) minmax(0, 1fr);
  gap: 12px;
  overflow-x: auto;
  -ms-overflow-style: none; /* Internet Explorer 10+ */
  scrollbar-width: none; /* Firefox, Safari 18.2+, Chromium 121+ */
  @media (max-width: 991px) {
    display: flex;
    padding: 0 8px;
  }
`;

const ProductCard = styled.div`
  background: #ffffff;
  border-radius: 16px;
  padding: 14px 12px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  min-height: 68px;
  flex: 1 1 50%;
  @media (max-width: 991px) {
    width: 250px;
  }
`;

const ProductInfoWrapper = styled(Link)`
  display: flex;
  align-items: center;
  gap: 12px;
  flex: 1;
  min-width: 0;
  overflow: hidden;
`;

const ProductImage = styled.div`
  width: 40px;
  height: 40px;
  border-radius: 8px;
  background: #f3f4f6;
  flex-shrink: 0;
  overflow: hidden;
  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    display: block;
  }
`;

const ProductInfo = styled.div`
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 2px;
`;

const ProductName = styled.span`
  font-size: 14px;
  font-weight: 500;
  line-height: 22px;
  color: #111827;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  max-width: 180px;
  @media (max-width: 991px) {
    max-width: 140px;
  }
`;

const ProductMeta = styled.span`
  font-size: 12px;
  font-weight: 400;
  line-height: 18px;
  color: #4b5563;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  display: flex;
  align-items: center;
  & > div {
    margin: 0 2px 0 0 !important;
  }
`;

const ProductAction = styled.button`
  background: none;
  border: none;
  padding: 0;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
`;

/* ─── Icons ──────────────────────────────────────────────────────────────────── */
const SaveIcon: FC = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 16 16" fill="none">
    <path d="M7.89974 7.43359V8.56641H1.43359V7.43359H7.89974Z" fill="#43A047" />
    <path d="M11.2331 3.43359V4.56641H1.43359V3.43359H11.2331Z" fill="#43A047" />
    <path d="M11.2331 11.4336V12.5664H1.43359V11.4336H11.2331Z" fill="#43A047" />
    <path d="M11.4336 5.43359H12.5664V10.5664H11.4336V5.43359Z" fill="#43A047" />
    <path d="M14.5664 7.43359V8.56641H9.43359V7.43359H14.5664Z" fill="#43A047" />
  </svg>
);

const HeartIcon: FC = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="18" height="16" viewBox="0 0 18 16" fill="none">
    <path
      d="M16.9883 4.81888C16.8277 0.762118 11.9777 -1.46644 9.0641 1.66014C8.99741 1.73147 8.63104 2.21553 8.58864 2.20439C7.46485 0.64502 5.7594 -0.10333 3.84483 0.317643C2.1155 0.697384 0.798237 2.12111 0.370948 3.81737C-0.575016 7.57314 2.32256 10.0577 4.82455 12.262C5.78904 13.1122 6.86138 14.0968 7.8662 14.8781C8.368 15.2682 8.84304 15.3123 9.34319 14.8781C11.8946 12.3787 16.7462 9.55101 17.0187 5.62825V4.87083L16.9883 4.81888ZM11.7933 11.472C10.7502 12.3931 9.66551 13.2697 8.61622 14.1838C8.57876 14.1904 8.47585 14.1021 8.44004 14.0729C7.14171 13.0124 5.76228 11.8175 4.52858 10.682C2.58602 8.8942 0.520375 6.83675 1.36507 3.92416C1.79566 2.43983 3.02113 1.31544 4.58826 1.19381C5.78739 1.10022 6.78069 1.59541 7.55377 2.48353C7.76617 2.72721 8.09879 3.35723 8.35112 3.46237C9.01388 3.73779 9.28144 2.91935 9.62435 2.51817C11.6105 0.196834 15.014 1.06187 15.8443 3.92416C16.7973 7.20907 13.9709 9.54895 11.7933 11.472Z"
      fill="#43A047"
    />
    <path
      d="M8.92693 1.53176C10.4414 -0.0932439 12.4677 -0.328531 14.1312 0.372905C15.7776 1.06726 17.0707 2.68016 17.1724 4.76174L17.1805 4.77557L17.2066 4.81952V5.63495L17.2057 5.64146C17.0643 7.67608 15.7378 9.40546 14.1654 10.9214C13.3767 11.6818 12.5175 12.3967 11.7004 13.0739C10.8807 13.7533 10.1057 14.3934 9.47462 15.0116L9.46648 15.0197C9.19128 15.2586 8.90362 15.3813 8.60141 15.3745C8.30285 15.3678 8.02006 15.2354 7.75099 15.0262C7.24554 14.6332 6.72268 14.189 6.20639 13.7372L4.70086 12.4025C3.45491 11.3049 2.08451 10.116 1.14942 8.72577C0.208426 7.32674 -0.299815 5.71264 0.189137 3.77134L0.282725 3.44582C0.803974 1.8337 2.11081 0.506457 3.80486 0.134461C5.72509 -0.287661 7.44264 0.425966 8.60629 1.92076C8.62675 1.89651 8.64991 1.8715 8.67221 1.84426C8.76914 1.72585 8.88818 1.57321 8.92693 1.53176ZM13.9855 0.718771C12.5034 0.0937398 10.716 0.273925 9.33383 1.65057L9.20118 1.78811C9.17325 1.81798 9.0762 1.94329 8.96274 2.08189C8.90986 2.14648 8.85498 2.21176 8.80974 2.26093C8.78774 2.28484 8.76366 2.30915 8.74138 2.32847C8.73084 2.3376 8.71448 2.35121 8.695 2.36265C8.68532 2.36832 8.66925 2.37666 8.64861 2.383C8.62954 2.38881 8.58935 2.39806 8.54119 2.38544L8.47608 2.36835L8.43621 2.31382C7.35212 0.809675 5.72016 0.097367 3.88543 0.500672C2.23362 0.863391 0.964961 2.22752 0.552907 3.8633C0.0960121 5.67761 0.563602 7.18344 1.4603 8.51662C2.36295 9.85863 3.69228 11.0145 4.94825 12.121C5.91723 12.9751 6.9825 13.9534 7.98129 14.73L8.15138 14.848C8.31625 14.9481 8.46758 14.9962 8.60955 14.9994C8.7943 15.0035 8.99316 14.9319 9.21664 14.739C9.86032 14.109 10.6472 13.4604 11.4611 12.7858C12.2797 12.1074 13.1287 11.4013 13.9058 10.652C15.465 9.14881 16.7001 7.50324 16.8314 5.61542V4.92206L16.8265 4.91392L16.8029 4.87323L16.8013 4.82603C16.724 2.87489 15.5196 1.36572 13.9855 0.718771ZM4.5739 1.00686C5.8415 0.907984 6.88956 1.4352 7.69483 2.36021L7.79005 2.47984C7.82228 2.52373 7.85456 2.57095 7.88689 2.61819C7.95446 2.71693 8.0211 2.81711 8.09116 2.91685C8.16049 3.01557 8.22663 3.10491 8.28891 3.17401C8.35481 3.24713 8.40024 3.27912 8.42319 3.28876L8.5127 3.31887C8.596 3.33864 8.66248 3.33082 8.7186 3.31073C8.79966 3.28162 8.88086 3.21862 8.96681 3.1203C9.05286 3.02182 9.13205 2.90145 9.21664 2.77118C9.29783 2.64615 9.38645 2.50774 9.48194 2.39602C11.5715 -0.0459858 15.1549 0.875943 16.0241 3.87144C16.521 5.58433 16.0277 7.04907 15.1468 8.31561C14.2714 9.57423 13.0001 10.6564 11.9177 11.6123C11.3943 12.0745 10.8602 12.5253 10.3275 12.9738C9.79451 13.4226 9.26275 13.8691 8.73976 14.3247L8.69988 14.3589L8.64861 14.3687C8.60481 14.3763 8.56814 14.3677 8.54851 14.3622C8.5267 14.356 8.50714 14.348 8.49317 14.341C8.46531 14.3269 8.43886 14.3093 8.4183 14.2946C8.3771 14.2652 8.33598 14.23 8.32146 14.2181C7.02033 13.1554 5.63786 11.9578 4.40138 10.8197C3.43173 9.92728 2.40974 8.94864 1.74024 7.82001C1.06593 6.6831 0.745675 5.38707 1.18523 3.87144C1.63498 2.32156 2.91985 1.13524 4.5739 1.00686ZM4.6032 1.38039H4.60239C3.12248 1.49542 1.95641 2.55794 1.54493 3.97642C1.13998 5.37297 1.42987 6.56208 2.06251 7.62876C2.70022 8.70381 3.68249 9.64851 4.65528 10.5438C5.88615 11.6767 7.2628 12.8694 8.55828 13.9276H8.55909C8.56934 13.936 8.58155 13.9451 8.59409 13.9553C9.08769 13.5272 9.58822 13.1062 10.0866 12.6865C10.6192 12.2381 11.1498 11.7904 11.6694 11.3316C12.7645 10.3645 13.9953 9.31484 14.8392 8.10158C15.6775 6.89631 16.1203 5.54825 15.6644 3.97642C14.8729 1.2477 11.6495 0.439718 9.76677 2.64016C9.69099 2.72888 9.61753 2.84308 9.53159 2.97544C9.449 3.10263 9.35553 3.24516 9.2492 3.36688C9.14266 3.48876 9.01083 3.60445 8.84474 3.66392C8.69385 3.71794 8.52998 3.7201 8.35483 3.66311L8.27914 3.63544C8.17586 3.59241 8.08447 3.50664 8.01059 3.42466C7.93296 3.33851 7.85621 3.23482 7.78435 3.13251C7.7132 3.03121 7.64004 2.92176 7.57765 2.83059C7.51247 2.73535 7.45747 2.65846 7.41244 2.60679C6.67172 1.75582 5.7335 1.29228 4.6032 1.38039Z"
      fill="#43A047"
    />
  </svg>
);

const BuildingIcon: FC = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 16 16" fill="none">
    <g clip-path="url(#clip0_7995_197647)">
      <path
        d="M11.4336 2.66536C11.4336 2.46203 11.3528 2.26682 11.209 2.12305C11.0652 1.97927 10.87 1.89844 10.6667 1.89844H5.33333C5.13 1.89844 4.93479 1.97927 4.79102 2.12305C4.64724 2.26682 4.56641 2.46203 4.56641 2.66536V14.099H11.4336V2.66536ZM12.5664 14.6654C12.5664 14.9783 12.313 15.2318 12 15.2318H4C3.68704 15.2318 3.43359 14.9783 3.43359 14.6654V2.66536C3.43359 2.16145 3.63326 1.67793 3.98958 1.32161C4.3459 0.965295 4.82942 0.765625 5.33333 0.765625H10.6667C11.1706 0.765625 11.6541 0.965296 12.0104 1.32161C12.3667 1.67793 12.5664 2.16145 12.5664 2.66536V14.6654Z"
        fill="#F38A3E"
      />
      <path
        d="M0.767578 13.3333V9.33333C0.767578 8.82942 0.967249 8.3459 1.32357 7.98958C1.67989 7.63326 2.16341 7.43359 2.66732 7.43359H4.00065V8.56641H2.66732C2.46399 8.56641 2.26878 8.64724 2.125 8.79102C1.98122 8.93479 1.90039 9.13 1.90039 9.33333V13.3333C1.90039 13.5367 1.98122 13.7319 2.125 13.8757C2.26878 14.0194 2.46399 14.1003 2.66732 14.1003H4.00065V15.2331H2.66732C2.16341 15.2331 1.67989 15.0334 1.32357 14.6771C0.967249 14.3208 0.767578 13.8372 0.767578 13.3333Z"
        fill="#F38A3E"
      />
      <path
        d="M14.1003 13.3333V7.33333C14.1003 7.13 14.0194 6.93479 13.8757 6.79102C13.7319 6.64724 13.5367 6.56641 13.3333 6.56641H12V5.43359H13.3333C13.8372 5.43359 14.3208 5.63326 14.6771 5.98958C15.0334 6.3459 15.2331 6.82942 15.2331 7.33333V13.3333C15.2331 13.8372 15.0334 14.3208 14.6771 14.6771C14.3208 15.0334 13.8372 15.2331 13.3333 15.2331H12V14.1003H13.3333C13.5367 14.1003 13.7319 14.0194 13.8757 13.8757C14.0194 13.7319 14.1003 13.5367 14.1003 13.3333Z"
        fill="#F38A3E"
      />
      <path d="M9.89909 4.10156V5.23438H6.09961V4.10156H9.89909Z" fill="#F38A3E" />
      <path d="M9.89909 7.43359V8.56641H6.09961V7.43359H9.89909Z" fill="#F38A3E" />
      <path d="M9.89909 10.7656V11.8984H6.09961V10.7656H9.89909Z" fill="#F38A3E" />
    </g>
    <defs>
      <clipPath id="clip0_7995_197647">
        <rect width="16" height="16" fill="white" />
      </clipPath>
    </defs>
  </svg>
);

const PlusIcon: FC = () => (
  <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M10 4V16M4 10H16" stroke="#E56910" strokeWidth="1.5" strokeLinecap="round" />
  </svg>
);

const HeartIconOutline: FC = () => (
  <svg width="13" height="11" viewBox="0 0 13 11" fill="none">
    <path
      d="M11.8914 3.37478C11.779 0.535045 8.38402 -1.02495 6.34448 1.16366C6.2978 1.21359 6.04134 1.55243 6.01166 1.54464C5.225 0.453077 4.03119 -0.0707688 2.69099 0.223912C1.48046 0.489732 0.558375 1.48634 0.259273 2.67372C-0.402902 5.30276 1.6254 7.04199 3.37679 8.58495C4.05194 9.18009 4.80257 9.86931 5.50595 10.4162C5.85721 10.6893 6.18974 10.7202 6.53984 10.4162C8.32581 8.66663 11.722 6.68727 11.9127 3.94134V3.41114L11.8914 3.37478ZM8.25493 8.03195C7.52475 8.67673 6.76547 9.29034 6.03097 9.93021C6.00475 9.93483 5.93271 9.87306 5.90764 9.85257C4.99881 9.11024 4.03321 8.27382 3.16961 7.47896C1.80982 6.2275 0.363872 4.78729 0.955161 2.74847C1.25657 1.70944 2.1144 0.922373 3.2114 0.83723C4.05078 0.771713 4.7461 1.11835 5.28725 1.74003C5.43593 1.91061 5.66876 2.35162 5.8454 2.42522C6.30932 2.61802 6.49662 2.04511 6.73665 1.76428C8.12699 0.139347 10.5094 0.744872 11.0906 2.74847C11.7577 5.04791 9.77925 6.68583 8.25493 8.03195Z"
      fill="white"
    />
    <path
      d="M6.24885 1.07223C7.30899 -0.0652707 8.72742 -0.229971 9.89184 0.261034C11.0443 0.74708 11.9495 1.87611 12.0207 3.33322L12.0264 3.3429L12.0446 3.37366V3.94446L12.044 3.94902C11.945 5.37325 11.0164 6.58382 9.91576 7.64499C9.36366 8.17726 8.76225 8.67767 8.19026 9.15174C7.61648 9.62729 7.07397 10.0754 6.63223 10.5081L6.62654 10.5138C6.43389 10.681 6.23253 10.7669 6.02099 10.7622C5.812 10.7574 5.61404 10.6648 5.42569 10.5184C5.07188 10.2432 4.70588 9.93231 4.34447 9.61601L3.2906 8.68177C2.41844 7.9134 1.45916 7.08119 0.804597 6.10804C0.145898 5.12872 -0.209871 3.99885 0.132396 2.63994L0.197907 2.41208C0.562782 1.28359 1.47756 0.35452 2.6634 0.0941228C4.00756 -0.201363 5.20985 0.298176 6.0244 1.34453C6.03872 1.32756 6.05493 1.31005 6.07055 1.29098C6.1384 1.20809 6.22173 1.10124 6.24885 1.07223ZM9.78987 0.50314C8.75239 0.0656178 7.50122 0.191747 6.53368 1.1554L6.44083 1.25167C6.42127 1.27259 6.35334 1.3603 6.27392 1.45732C6.2369 1.50254 6.19849 1.54823 6.16682 1.58265C6.15142 1.59939 6.13456 1.61641 6.11897 1.62993C6.11159 1.63632 6.10014 1.64585 6.0865 1.65386C6.07972 1.65782 6.06848 1.66366 6.05403 1.6681C6.04068 1.67217 6.01255 1.67864 5.97883 1.66981L5.93326 1.65784L5.90535 1.61968C5.14648 0.566772 4.00411 0.0681569 2.7198 0.35047C1.56354 0.604373 0.675473 1.55926 0.387035 2.70431C0.0672085 3.97433 0.394522 5.02841 1.02221 5.96164C1.65407 6.90104 2.5846 7.71011 3.46378 8.48467C4.14206 9.08257 4.88775 9.76735 5.5869 10.311L5.70596 10.3936C5.82138 10.4637 5.9273 10.4973 6.02668 10.4996C6.15601 10.5025 6.29521 10.4524 6.45165 10.3173C6.90222 9.8763 7.45306 9.42227 8.02278 8.95008C8.59578 8.47517 9.19006 7.98087 9.73404 7.45643C10.8255 6.40416 11.6901 5.25227 11.782 3.93079V3.44544L11.7786 3.43974L11.762 3.41126L11.7609 3.37822C11.7068 2.01242 10.8637 0.956006 9.78987 0.50314ZM3.20173 0.7048C4.08905 0.635589 4.82269 1.00464 5.38638 1.65215L5.45303 1.73589C5.47559 1.76661 5.49819 1.79966 5.52082 1.83273C5.56812 1.90185 5.61477 1.97198 5.66381 2.0418C5.71234 2.1109 5.75864 2.17343 5.80224 2.22181C5.84837 2.27299 5.88017 2.29538 5.89623 2.30213L5.95889 2.32321C6.0172 2.33705 6.06374 2.33158 6.10302 2.31751C6.15976 2.29713 6.21661 2.25303 6.27676 2.18421C6.337 2.11527 6.39244 2.03102 6.45165 1.93983C6.50848 1.8523 6.57051 1.75542 6.63736 1.67721C8.10005 -0.03219 10.6084 0.61316 11.2169 2.71001C11.5647 3.90903 11.2194 4.93435 10.6028 5.82093C9.98998 6.70196 9.10009 7.45948 8.34236 8.12863C7.97598 8.45216 7.60211 8.76772 7.22924 9.08167C6.85615 9.3958 6.48393 9.70838 6.11783 10.0273L6.08992 10.0512L6.05403 10.0581C6.02337 10.0634 5.9977 10.0574 5.98396 10.0535C5.96869 10.0492 5.955 10.0436 5.94522 10.0387C5.92572 10.0289 5.9072 10.0165 5.89281 10.0062C5.86397 9.98565 5.83519 9.961 5.82502 9.95268C4.91423 9.20875 3.9465 8.37042 3.08096 7.57378C2.40221 6.9491 1.68682 6.26405 1.21817 5.47401C0.746151 4.67817 0.521972 3.77095 0.829662 2.71001C1.14449 1.62509 2.04389 0.794665 3.20173 0.7048ZM3.22224 0.966274H3.22167C2.18573 1.04679 1.36949 1.79056 1.08145 2.78349C0.797985 3.76108 1.00091 4.59346 1.44376 5.34013C1.89015 6.09267 2.57774 6.75395 3.2587 7.38066C4.12031 8.1737 5.08396 9.00861 5.99079 9.74931H5.99136C5.99854 9.75518 6.00709 9.76159 6.01586 9.76868C6.36138 9.46906 6.71176 9.17432 7.06062 8.88058C7.43347 8.56664 7.80489 8.25327 8.16861 7.93209C8.93518 7.25514 9.79673 6.52039 10.3874 5.67111C10.9742 4.82742 11.2842 3.88378 10.9651 2.78349C10.411 0.873387 8.15467 0.307803 6.83674 1.84811C6.78369 1.91021 6.73227 1.99016 6.67211 2.08281C6.6143 2.17184 6.54887 2.27161 6.47444 2.35682C6.39986 2.44213 6.30758 2.52311 6.19132 2.56474C6.08569 2.60256 5.97099 2.60407 5.84838 2.56417L5.7954 2.54481C5.7231 2.51468 5.65913 2.45465 5.60741 2.39726C5.55307 2.33696 5.49935 2.26437 5.44905 2.19276C5.39924 2.12185 5.34802 2.04523 5.30435 1.98141C5.25873 1.91474 5.22023 1.86092 5.18871 1.82475C4.6702 1.22908 4.01345 0.904595 3.22224 0.966274Z"
      fill="white"
    />
  </svg>
);

const ChevronRightIcon: FC = () => (
  <svg width="14" height="14" viewBox="0 0 14 14" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M5 3L9 7L5 11" stroke="#E56910" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

const AddedToFavoriteSection = styled.div`
  position: relative;
  color: #2f382c;
  display: flex;
  flex-direction: column;
  flex-grow: 1;
  align-items: center;
  gap: 16px;
`;

const AddedToFavoriteImage = styled.img`
  width: 115px;
  @media (max-width: 1023px) {
    width: 92px;
  }
`;

const AddedToFavoriteText = styled.div`
  font-size: 16px;
  font-weight: 600;
  color: #242527;
`;

const AddedToFavoriteTextbox = styled.div`
  text-align: center;
`;

const AddedToFavoriteSubText = styled.div`
  font-size: 12px;
  color: #585a60;
  margin: 4px 0 6px;
  text-align: center;
`;

const GoToFavoriteButtonMobile = styled(Link)`
  display: flex;
  gap: 6px;
  align-items: center;
  font-size: 14px;
  font-weight: 500;
  color: #ffffff;
  justify-content: center;
  cursor: pointer;
  padding: 5px 12px;
  background: #43a047;
  border-radius: 6px;
  width: fit-content;
  margin: 0 auto;
  & > div {
    margin-top: 2px;
  }
  &:hover {
    color: #ffffff;
    opacity: 0.9;
  }
`;
const ProductCardSkeleton: FC = () => (
  <ProductCard>
    <Skeleton
      active
      avatar={{ shape: 'square', size: 40 }}
      title={{ width: 120, style: { marginTop: 0 } }}
      paragraph={{ rows: 1, style: { marginTop: 12 } }}
    />
  </ProductCard>
);
/* ─── Sub-components ─────────────────────────────────────────────────────────── */
interface IProductItemProps {
  data: Product;
  imageUrl?: string;
  actionIcon: React.ReactNode;
}

const HeartIconGoToFavorite: FC = () => {
  return (
    <svg xmlns="http://www.w3.org/2000/svg" width="13" height="11" viewBox="0 0 13 11" fill="none">
      <path
        d="M11.8914 3.37283C11.779 0.533092 8.38402 -1.0269 6.34448 1.16171C6.2978 1.21164 6.04134 1.55048 6.01166 1.54269C5.225 0.451124 4.03119 -0.0727219 2.69099 0.221959C1.48046 0.487778 0.558375 1.48438 0.259273 2.67177C-0.402902 5.30081 1.6254 7.04003 3.37679 8.583C4.05194 9.17813 4.80257 9.86736 5.50595 10.4143C5.85721 10.6873 6.18974 10.7182 6.53984 10.4143C8.32581 8.66468 11.722 6.68532 11.9127 3.93939V3.40919L11.8914 3.37283ZM8.25493 8.03C7.52475 8.67478 6.76547 9.28838 6.03097 9.92825C6.00475 9.93287 5.93271 9.87111 5.90764 9.85062C4.99881 9.10829 4.03321 8.27186 3.16961 7.477C1.80982 6.22555 0.363872 4.78533 0.955161 2.74652C1.25657 1.70749 2.1144 0.92042 3.2114 0.835277C4.05078 0.76976 4.7461 1.11639 5.28725 1.73808C5.43593 1.90866 5.66876 2.34967 5.8454 2.42327C6.30932 2.61606 6.49662 2.04315 6.73665 1.76232C8.12699 0.137394 10.5094 0.742919 11.0906 2.74652C11.7577 5.04596 9.77925 6.68388 8.25493 8.03Z"
        fill="white"
      />
      <path
        d="M6.24885 1.07223C7.30899 -0.0652707 8.72742 -0.229971 9.89184 0.261034C11.0443 0.74708 11.9495 1.87611 12.0207 3.33322L12.0264 3.3429L12.0446 3.37366V3.94446L12.044 3.94902C11.945 5.37325 11.0164 6.58382 9.91576 7.64499C9.36366 8.17726 8.76225 8.67767 8.19026 9.15174C7.61648 9.62729 7.07397 10.0754 6.63223 10.5081L6.62654 10.5138C6.43389 10.681 6.23253 10.7669 6.02099 10.7622C5.812 10.7574 5.61404 10.6648 5.42569 10.5184C5.07188 10.2432 4.70588 9.93231 4.34447 9.61601L3.2906 8.68177C2.41844 7.9134 1.45916 7.08119 0.804597 6.10804C0.145898 5.12872 -0.209871 3.99885 0.132396 2.63994L0.197907 2.41208C0.562782 1.28359 1.47756 0.35452 2.6634 0.0941228C4.00756 -0.201363 5.20985 0.298176 6.0244 1.34453C6.03872 1.32756 6.05493 1.31005 6.07055 1.29098C6.1384 1.20809 6.22173 1.10124 6.24885 1.07223ZM9.78987 0.50314C8.75239 0.0656178 7.50122 0.191747 6.53368 1.1554L6.44083 1.25167C6.42127 1.27259 6.35334 1.3603 6.27392 1.45732C6.2369 1.50254 6.19849 1.54823 6.16682 1.58265C6.15142 1.59939 6.13456 1.61641 6.11897 1.62993C6.11159 1.63632 6.10014 1.64585 6.0865 1.65386C6.07972 1.65782 6.06848 1.66366 6.05403 1.6681C6.04068 1.67217 6.01255 1.67864 5.97883 1.66981L5.93326 1.65784L5.90535 1.61968C5.14648 0.566772 4.00411 0.0681569 2.7198 0.35047C1.56354 0.604373 0.675473 1.55926 0.387035 2.70431C0.0672085 3.97433 0.394522 5.02841 1.02221 5.96164C1.65407 6.90104 2.5846 7.71011 3.46378 8.48467C4.14206 9.08257 4.88775 9.76735 5.5869 10.311L5.70596 10.3936C5.82138 10.4637 5.9273 10.4973 6.02668 10.4996C6.15601 10.5025 6.29521 10.4524 6.45165 10.3173C6.90222 9.8763 7.45306 9.42227 8.02278 8.95008C8.59578 8.47517 9.19006 7.98087 9.73404 7.45643C10.8255 6.40416 11.6901 5.25227 11.782 3.93079V3.44544L11.7786 3.43974L11.762 3.41126L11.7609 3.37822C11.7068 2.01242 10.8637 0.956006 9.78987 0.50314ZM3.20173 0.7048C4.08905 0.635589 4.82269 1.00464 5.38638 1.65215L5.45303 1.73589C5.47559 1.76661 5.49819 1.79966 5.52082 1.83273C5.56812 1.90185 5.61477 1.97198 5.66381 2.0418C5.71234 2.1109 5.75864 2.17343 5.80224 2.22181C5.84837 2.27299 5.88017 2.29538 5.89623 2.30213L5.95889 2.32321C6.0172 2.33705 6.06374 2.33158 6.10302 2.31751C6.15976 2.29713 6.21661 2.25303 6.27676 2.18421C6.337 2.11527 6.39244 2.03102 6.45165 1.93983C6.50848 1.8523 6.57051 1.75542 6.63736 1.67721C8.10005 -0.03219 10.6084 0.61316 11.2169 2.71001C11.5647 3.90903 11.2194 4.93435 10.6028 5.82093C9.98998 6.70196 9.10009 7.45948 8.34236 8.12863C7.97598 8.45216 7.60211 8.76772 7.22924 9.08167C6.85615 9.3958 6.48393 9.70838 6.11783 10.0273L6.08992 10.0512L6.05403 10.0581C6.02337 10.0634 5.9977 10.0574 5.98396 10.0535C5.96869 10.0492 5.955 10.0436 5.94522 10.0387C5.92572 10.0289 5.9072 10.0165 5.89281 10.0062C5.86397 9.98565 5.83519 9.961 5.82502 9.95268C4.91423 9.20875 3.9465 8.37042 3.08096 7.57378C2.40221 6.9491 1.68682 6.26405 1.21817 5.47401C0.746151 4.67817 0.521972 3.77095 0.829662 2.71001C1.14449 1.62509 2.04389 0.794665 3.20173 0.7048ZM3.22224 0.966274H3.22167C2.18573 1.04679 1.36949 1.79056 1.08145 2.78349C0.797985 3.76108 1.00091 4.59346 1.44376 5.34013C1.89015 6.09267 2.57774 6.75395 3.2587 7.38066C4.12031 8.1737 5.08396 9.00861 5.99079 9.74931H5.99136C5.99854 9.75518 6.00709 9.76159 6.01586 9.76868C6.36138 9.46906 6.71176 9.17432 7.06062 8.88058C7.43347 8.56664 7.80489 8.25327 8.16861 7.93209C8.93518 7.25514 9.79673 6.52039 10.3874 5.67111C10.9742 4.82742 11.2842 3.88378 10.9651 2.78349C10.411 0.873387 8.15467 0.307803 6.83674 1.84811C6.78369 1.91021 6.73227 1.99016 6.67211 2.08281C6.6143 2.17184 6.54887 2.27161 6.47444 2.35682C6.39986 2.44213 6.30758 2.52311 6.19132 2.56474C6.08569 2.60256 5.97099 2.60407 5.84838 2.56417L5.7954 2.54481C5.7231 2.51468 5.65913 2.45465 5.60741 2.39726C5.55307 2.33696 5.49935 2.26437 5.44905 2.19276C5.39924 2.12185 5.34802 2.04523 5.30435 1.98141C5.25873 1.91474 5.22023 1.86092 5.18871 1.82475C4.6702 1.22908 4.01345 0.904595 3.22224 0.966274Z"
        fill="white"
      />
    </svg>
  );
};

const ProductItem: FC<IProductItemProps> = ({ data, actionIcon }) => {
  const generateUomInfo = (product) => {
    const { uomInfo } = useProductUomInfo(product);
    return uomInfo;
  };
  const lang = localStorage.getItem('lang') || 'vi';
  return (
    <ProductCard>
      <ProductInfoWrapper to={`/products/${generateProductSlug(data?.name, data?.supplierInternalCode, lang)}`}>
        <ProductImage>
          {data?.imageUrl && <img src={data?.imageUrl} alt={data?.name || 'favorite image'} />}
        </ProductImage>
        <ProductInfo>
          <ProductName>{data?.name}</ProductName>
          <ProductMeta>
            {data?.uom ? generateUomInfo(data) : ''}
            {` • `}
            {formatMoney(
              calculateVAT(
                data?.priceV2?.originalPrice || 0,
                data?.priceV2?.vat || 0,
                data?.priceV2?.vatIncluded || false,
              ).beforeVat,
            )}
            ₫
          </ProductMeta>
        </ProductInfo>
      </ProductInfoWrapper>
      <ProductAction type="button">{actionIcon}</ProductAction>
    </ProductCard>
  );
};
/* ─── FavoritePanel ──────────────────────────────────────────────────────────── */
interface IFavoritePanelProps {
  isAdded?: boolean;
  store: Store;
}

const FavoritePanel: FC<IFavoritePanelProps> = ({ isAdded = false, store }) => {
  const [data, setData] = useState([] as Product[]);
  const [isAddedAll, setIsAddedAll] = useState(false);
  const [isFetching, setIsFetching] = useState(true);

  const params: QueryProductSearchArgs = {
    sort: [{ field: ProductSortField.Name, order: SortDirection.Asc }],
    filter: {
      isMightFavorite: true,
      storeUuid: store.id,
    },
    pagination: { page: 0, size: 100 },
  };
  const [fetchProducts] = useLazyQuerySaga<GQuery<'productSearch'>, QueryProductSearchArgs>(productSearch);
  const [addItemToFavorite] = useMutationSaga<GMutation<'favoriteAddItem'>, MutationFavoriteAddItemArgs>(
    favoriteAddItem,
  );
  const [putAllItemToFavorite] = useMutationSaga<GMutation<'putItemsToFavorite'>, MutationPutItemsToFavoriteArgs>(
    putItemsToFavorite,
  );

  const handleAddToFavorite = async (product: Product) => {
    const currentData = [...data];
    const newData = currentData.filter((item) => item.id !== product.id);
    setData(newData);
    if (currentData?.length === 1) {
      setIsAddedAll(true);
    }
    message.success(translations(messages.addItemToFavorite));
    const res = await addItemToFavorite({ variables: { id: product.id } });
    if (!res?.success) {
      const restored = [...newData, product];
      setData(restored);
      setIsAddedAll(false);
      message.error(translations(messages.failToAddFavorite));
    }
  };

  const handleAddAllItemToFavorite = async () => {
    const res = await putAllItemToFavorite({ variables: { itemUuids: data.map((it) => it.id) } });
    if (res?.success) {
      message.success(translations(messages.addAllItemToFavorite));
      setData([]);
      setIsAddedAll(true);
    }
  };

  useEffect(() => {
    (async () => {
      const purchasedItems = await fetchProducts({
        variables: {
          ...params,
        },
      });
      if (purchasedItems?.data) {
        const sliced = purchasedItems.data.slice(0, 4) as Product[];
        setData(sliced);
      }
      setIsFetching(false);
    })();
  }, []);

  if (!isFetching && !data?.length && !isAddedAll) {
    return null;
  }

  return (
    <CardBase variant="green">
      {!isAddedAll && !isFetching && (
        <CardHeader>
          <HeaderLeft>
            <IconCircle variant="green">
              <SaveIcon />
            </IconCircle>
            <HeaderText>
              <CardTitle>
                <FormattedMessage {...messages.addToListTitle} />
              </CardTitle>
              <CardSubtitle>
                <FormattedMessage {...messages.addToListSubtitle} />
              </CardSubtitle>
            </HeaderText>
          </HeaderLeft>
          <AddAllButton onClick={handleAddAllItemToFavorite} type="button">
            <HeartIconOutline />
            <FormattedMessage {...messages.addAllBtn} />
          </AddAllButton>
        </CardHeader>
      )}

      {isAddedAll ? (
        <AddedToFavoriteSection>
          <AddedToFavoriteImage src={FavoriteImage} />
          <AddedToFavoriteTextbox>
            <AddedToFavoriteText>
              <FormattedMessage {...messages.addedToFavorite} />
            </AddedToFavoriteText>
            <AddedToFavoriteSubText>
              <FormattedMessage {...messages.addedToFavoriteSubText} />
            </AddedToFavoriteSubText>
            <GoToFavoriteButtonMobile to={'/favorite'}>
              <HeartIconGoToFavorite />
              <FormattedMessage {...messages.goToFavorite} />
            </GoToFavoriteButtonMobile>
          </AddedToFavoriteTextbox>
        </AddedToFavoriteSection>
      ) : isFetching ? (
        <ProductGrid>
          {Array.from({ length: 4 }).map((_, i) => (
            <ProductCardSkeleton key={i} />
          ))}
        </ProductGrid>
      ) : (
        <ProductGrid>
          {data.map((p) => (
            <ProductItem
              key={p.id}
              data={p}
              actionIcon={
                <div onClick={() => handleAddToFavorite(p)}>
                  <HeartIcon />
                </div>
              }
            />
          ))}
        </ProductGrid>
      )}
    </CardBase>
  );
};

/* ─── RecommendedPanel ───────────────────────────────────────────────────────── */
const RecommendedPanel: FC<{
  cartItems: CartItem[];
  store: Store;
  updateItemCart: (payload: any) => void;
  buyer: Buyer;
}> = ({ cartItems, store, updateItemCart, buyer }) => {
  const [data, setData] = useState([] as Product[]);
  const [restData, setRestData] = useState([] as Product[]);
  const [isFetching, setIsFetching] = useState(true);
  const lang = localStorage.getItem('lang') || 'vi';

  const params: QueryProductSearchArgs = {
    sort: [{ field: ProductSortField.Name, order: SortDirection.Asc }],
    filter: {
      isOnboardingBuyer: false,
      isRecommendation: true,
    },
    pagination: { page: 0, size: 20 },
  };
  const [fetchProducts] = useLazyQuerySaga<GQuery<'productSearch'>, QueryProductSearchArgs>(productSearch, {
    variables: { ...params },
  });
  const [addToCard] = useMutationSaga<GMutation<'cartPutItemLight'>, MutationCartPutItemLightArgs>(cartPutItemLight);

  const handleAddToCart = async (product: Product) => {
    const targetProduct = cartItems?.find((cart) => cart.product?.id === product?.id);
    const res = await addToCard({
      variables: {
        input: {
          productId: product?.id,
          quantity: targetProduct?.product?.id ? Number(targetProduct?.quantity) + 1 : 1,
          storeId: store?.id,
          supplierId: 654,
        },
      },
    });
    if (res?.success) {
      message.success(translations(messages.addedToCart));
      updateItemCart(res?.cart);
      const newMain = data.filter((val) => val.id !== product?.id);
      if (restData[0]) newMain.push(restData[0]);
      const newRest = restData.slice(1);
      setData(newMain);
      setRestData(newRest);
    } else {
      message.error(translations(messages.failToAddToCart));
    }
  };

  useEffect(() => {
    (async () => {
      const products = await fetchProducts({
        variables: {
          ...params,
        },
      });
      if (products?.data) {
        const main = products.data.slice(0, 4) as Product[];
        const rest = products.data.slice(4) as Product[];
        setData(main);
        setRestData(rest);
      }
      setIsFetching(false);
    })();
  }, []);
  return (
    <CardBase variant="yellow">
      <CardHeader>
        <HeaderLeft>
          <IconCircle variant="yellow">
            <BuildingIcon />
          </IconCircle>
          <HeaderText>
            <CardTitle>
              <FormattedMessage {...messages.recommendedTitle} />
            </CardTitle>
            <CardSubtitle>
              {lang === 'vi' ? (
                <span>
                  Được mua nhiều nhất trong danh mục
                  <br />
                  kinh doanh&nbsp;
                  {buyer?.businessCategory ? (
                    <>
                      {utilsMessages[buyer?.businessCategory] ? (
                        <FormattedMessage {...utilsMessages[buyer?.businessCategory]} />
                      ) : (
                        buyer?.businessCategory
                      )}
                    </>
                  ) : (
                    ''
                  )}
                </span>
              ) : (
                <span>
                  Recommend for&nbsp;
                  {buyer?.businessCategory ? (
                    <>
                      {utilsMessages[buyer?.businessCategory] ? (
                        <FormattedMessage {...utilsMessages[buyer?.businessCategory]} />
                      ) : (
                        buyer?.businessCategory
                      )}
                    </>
                  ) : (
                    ''
                  )}
                </span>
              )}
            </CardSubtitle>
          </HeaderText>
        </HeaderLeft>
        <ViewAllLink to="/RECOMMENDED_FOR_YOU">
          <FormattedMessage {...messages.viewAll} />
          <ChevronRightIcon />
        </ViewAllLink>
      </CardHeader>

      <ProductGrid>
        {isFetching
          ? Array.from({ length: 4 }).map((_, i) => <ProductCardSkeleton key={i} />)
          : data.map((p) => (
              <ProductItem
                key={p.id}
                data={p}
                actionIcon={
                  <div onClick={() => handleAddToCart(p)}>
                    <PlusIcon />
                  </div>
                }
              />
            ))}
      </ProductGrid>
    </CardBase>
  );
};

/* ─── PersonalizedSections ───────────────────────────────────────────────────── */
const PersonalizedSections: FC<IProps> = ({ store, cart, updateItemCart, buyer }) => {
  return (
    <Wrapper>
      {store?.id && <FavoritePanel store={store} />}
      <RecommendedPanel
        buyer={buyer}
        updateItemCart={updateItemCart}
        cartItems={cart?.cartSet?.[0]?.items || []}
        store={store}
      />
    </Wrapper>
  );
};

const mapStateToProps = createStructuredSelector<RootState, StateProps>({
  store: selectStore(),
  permissions: selectUserPermissions(),
  cart: selectCart(),
  buyer: selectBuyer(),
});

const mapDispatchToProps = (dispatch: Dispatch): IDispatchProps => ({
  updateItemCart: (payload: any) => dispatch(updateItemCart.success(payload)),
});

const withConnect = connect(mapStateToProps, mapDispatchToProps);

export default withConnect(PersonalizedSections);
